import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  adminPhotoUrl,
  CSRF_STORAGE_KEY,
  csrfRequestConfig,
  rememberCsrfToken,
  restoreAdminSession,
} from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "scheduled", "completed"];

const statusColor = {
  new: "text-volt border-volt/50",
  contacted: "text-silver border-silver/40",
  scheduled: "text-emerald-400 border-emerald-400/40",
  completed: "text-silver/50 border-graphite",
};

export default function Admin() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [csrfToken, setCsrfToken] = useState(() => sessionStorage.getItem(CSRF_STORAGE_KEY) || "");

  const acceptAuth = useCallback((data) => {
    const { csrf_token: nextCsrfToken, ...currentUser } = data;
    setCsrfToken(rememberCsrfToken(nextCsrfToken));
    setUser(currentUser);
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/requests`, { withCredentials: true });
      setRequests(data);
    } catch {
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    const storedCsrfToken = sessionStorage.getItem(CSRF_STORAGE_KEY) || "";
    restoreAdminSession(axios, API, storedCsrfToken)
      .then((data) => {
        acceptAuth(data);
        loadRequests();
      })
      .catch(() => {
        setCsrfToken(rememberCsrfToken(""));
        setUser(false);
      });
  }, [acceptAuth, loadRequests]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      acceptAuth(data);
      loadRequests();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    }
  };

  const onLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, csrfRequestConfig(csrfToken));
    setCsrfToken(rememberCsrfToken(""));
    setUser(false);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/requests/${id}`, { status }, csrfRequestConfig(csrfToken));
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwMsg("");
    if (newPw !== confirmPw) {
      setPwMsg("New passwords do not match.");
      return;
    }
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPw, new_password: newPw },
        csrfRequestConfig(csrfToken)
      );
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setPwMsg("Password updated. Use it on your next sign in.");
      toast.success("Admin password updated");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPwMsg(typeof detail === "string" ? detail : "Could not update password.");
    }
  };

  const inputCls =
    "w-full bg-onyx border border-graphite px-4 py-3.5 text-sm text-ivory placeholder:text-silver/40 focus:outline-none focus:border-volt focus:ring-1 focus:ring-volt transition-colors duration-300";

  if (user === null) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <p className="font-accent text-xs tracking-[0.3em] uppercase text-silver/50">Loading…</p>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="admin-login-page">
        <form onSubmit={onLogin} data-testid="admin-login-form" className="w-full max-w-sm border border-graphite bg-onyx/40 p-9">
          <p className="font-display font-semibold text-2xl metallic-text tracking-[0.1em]">DARK</p>
          <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-volt mt-1 mb-8">Mobile Detail · Admin</p>
          <label htmlFor="admin-email" className="block font-accent text-[10px] tracking-[0.25em] uppercase text-silver/60 mb-2">Email</label>
          <input id="admin-email" data-testid="admin-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
          <label htmlFor="admin-password" className="block font-accent text-[10px] tracking-[0.25em] uppercase text-silver/60 mb-2 mt-5">Password</label>
          <input id="admin-password" data-testid="admin-password-input" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <p data-testid="admin-login-error" className="text-red-400 text-xs mt-4">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" className="w-full mt-7 font-accent text-xs tracking-[0.25em] uppercase bg-volt text-white py-4 hover:bg-[#4A8FFF] transition-colors duration-300">
            Sign In
          </button>
          <a href="/" data-testid="admin-back-link" className="block text-center font-accent text-[10px] tracking-[0.25em] uppercase text-silver/40 hover:text-silver mt-6 transition-colors duration-300">
            Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-5 sm:px-8 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="font-display font-semibold text-2xl metallic-text tracking-[0.1em]">DARK</p>
            <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-volt mt-1">Mobile Detail · Service Requests</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-view-site-link" className="font-accent text-[10px] tracking-[0.25em] uppercase text-silver/50 hover:text-ivory transition-colors duration-300">
              View Site
            </a>
            <button data-testid="admin-logout-button" onClick={onLogout} className="font-accent text-[10px] tracking-[0.25em] uppercase border border-graphite text-silver px-5 py-2.5 hover:border-volt hover:text-ivory transition-colors duration-300">
              Log Out
            </button>
          </div>
        </div>

        <details className="border border-graphite bg-onyx/40 mb-8" data-testid="security-panel">
          <summary data-testid="change-password-toggle" className="cursor-pointer px-6 py-4 font-accent text-[10px] tracking-[0.3em] uppercase text-silver/60 hover:text-ivory transition-colors duration-300 list-none flex items-center justify-between">
            Security — Change Password
            <span className="text-volt font-accent text-xs">+</span>
          </summary>
          <form onSubmit={onChangePassword} data-testid="change-password-form" className="px-6 pb-6 grid sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="current-pw" className="block font-accent text-[10px] tracking-[0.25em] uppercase text-silver/60 mb-2">Current Password</label>
              <input id="current-pw" data-testid="current-password-input" type="password" required className={inputCls} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} autoComplete="current-password" />
            </div>
            <div>
              <label htmlFor="new-pw" className="block font-accent text-[10px] tracking-[0.25em] uppercase text-silver/60 mb-2">New Password</label>
              <input id="new-pw" data-testid="new-password-input" type="password" required minLength={10} className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" />
            </div>
            <div>
              <label htmlFor="confirm-pw" className="block font-accent text-[10px] tracking-[0.25em] uppercase text-silver/60 mb-2">Confirm New Password</label>
              <input id="confirm-pw" data-testid="confirm-password-input" type="password" required minLength={10} className={inputCls} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="sm:col-span-3 flex items-center gap-5">
              <button type="submit" data-testid="change-password-submit" className="font-accent text-[10px] tracking-[0.25em] uppercase bg-volt text-white px-7 py-3.5 hover:bg-[#4A8FFF] transition-colors duration-300">
                Update Password
              </button>
              {pwMsg && <p data-testid="change-password-message" className="text-silver/70 text-xs">{pwMsg}</p>}
            </div>
          </form>
        </details>

        {requests.length === 0 ? (
          <div className="border border-dashed border-graphite p-14 text-center" data-testid="admin-empty-state">
            <p className="font-display italic text-xl text-silver/50">No requests yet. They will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-5" data-testid="admin-requests-list">
            {requests.map((r) => (
              <article key={r.id} data-testid={`admin-request-${r.id}`} className="border border-graphite bg-onyx/40 p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl text-ivory">{r.full_name}</h3>
                    <p className="text-silver/60 text-sm mt-1">
                      {r.vehicle_year} {r.vehicle_make} {r.vehicle_model} · {r.service_interest}
                    </p>
                  </div>
                  <select
                    data-testid={`admin-status-${r.id}`}
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className={`bg-ink border px-3 py-2 font-accent text-[10px] tracking-[0.25em] uppercase focus:outline-none ${statusColor[r.status] || "text-silver border-graphite"}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="grid sm:grid-cols-3 gap-x-8 gap-y-2 mt-5 text-sm text-silver/75">
                  <span>{r.phone}</span>
                  <span>{r.email}</span>
                  <span>{r.service_location || "—"}</span>
                  <span>Condition: {r.vehicle_condition || "—"}</span>
                  <span>Preferred: {r.preferred_day || "—"}</span>
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                </div>
                {r.notes && <p className="text-silver/60 text-sm mt-4 leading-relaxed">"{r.notes}"</p>}
                {r.photos?.length > 0 && (
                  <div className="flex gap-3 mt-5 flex-wrap">
                    {r.photos.map((p) => {
                      const photoUrl = adminPhotoUrl(API, r, p);
                      return (
                        <a key={p} href={photoUrl} target="_blank" rel="noopener noreferrer">
                          <img src={photoUrl} alt="Customer vehicle upload" className="w-20 h-20 object-cover border border-graphite hover:border-volt transition-colors duration-300" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
